// Signup sub-phase machine (auth-choice → email/google → optin), extracted from
// useSignupFlow. The sub-conversation is interleaved into the SHARED chat timeline (per
// the M1 decision — not a self-contained component), so this composable takes the
// orchestrator's timeline / state / api / tracking plus a couple of helpers as `ctx`
// rather than owning its own conversation or state. See useSignupFlow.
import {
  EMAIL_FORM_INTRO,
  EMAIL_ACCOUNT_CREATED,
  EMAIL_ALREADY_EXISTS,
  SIGNUP_FAILED,
  GOOGLE_POPUP_BLOCKED,
} from '../config/wordings'
import { translateUniverse } from '../config/flow-graph'
import type { useSignupState } from './useSignupState'
import type { useChatTimeline } from './useChatTimeline'
import type { useSignupApi } from './useSignupApi'
import type { useSignupTracking } from './useSignupTracking'
import { geoToRegistrationPayload } from './useAddressAutocomplete'
import { getFunnelVariant } from './useFunnelVariant'
import { readCsrfMetaToken } from '@twg/api-client'
import { runGoogleOAuthPopup, submitOAuthForm, SIGNUP_OAUTH_WINDOW_NAME } from './useGoogleOAuthPopup'

const GOOGLE_ICON = new URL('../assets/google-logo.svg', import.meta.url).href

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

// Popup dimensions/position for chooseGoogle's window.open — centers the OAuth popup on
// the CURRENT window rather than assuming a single primary display at (0,0), so a
// dual-monitor setup gets the popup centered on whichever screen the signup page is on.
// Fallbacks (screenLeft/screenTop, screen.width/height) cover engines where
// screenX/screenY/outerWidth/outerHeight might be unavailable.
function oauthPopupFeatures(): string {
  const w = 500
  const h = 640
  const screenX = window.screenX ?? window.screenLeft ?? 0
  const screenY = window.screenY ?? window.screenTop ?? 0
  const outerWidth = window.outerWidth ?? window.screen.width
  const outerHeight = window.outerHeight ?? window.screen.height
  const left = Math.round(screenX + (outerWidth - w) / 2)
  const top = Math.round(screenY + (outerHeight - h) / 2)
  return `popup,width=${w},height=${h},left=${left},top=${top}`
}

export interface SignupAuthContext {
  state: ReturnType<typeof useSignupState>
  timeline: ReturnType<typeof useChatTimeline>
  api: ReturnType<typeof useSignupApi>
  tracking: ReturnType<typeof useSignupTracking>
  stepStartIdx: Record<string, number>
  finishSignup: (opts?: { skipTracking?: boolean }) => Promise<void>
}

export function useSignupAuth(ctx: SignupAuthContext) {
  const { state, timeline, api, tracking, stepStartIdx, finishSignup } = ctx
  const {
    submitInFlight,
    authMethod,
    cguAccepted,
    signupSubPhase,
    email,
    password,
    emailError,
    universe,
    role,
    typeGarde,
    addressGeo,
    accountRedirectUrl,
    snapshot,
  } = state

  // Explicit CGUA acceptance (the cgua-consent card's sole intent). Records consent,
  // swaps the card for auth-choice, and hands off to the method-choice sub-phase.
  async function acceptCgua(): Promise<void> {
    if (submitInFlight.value) return
    cguAccepted.value = true
    timeline.removeFirstOfKind('cgua-consent')
    // Tag the bubble with the 'cgua' step so it's editable like every other step (click →
    // goBackToStep('cgua') re-shows the consent card); stepStartIdx marks where to rewind to.
    stepStartIdx['cgua'] = timeline.messages.value.length
    timeline.addUser("✅ J'accepte les conditions", { step: 'cgua' })
    signupSubPhase.value = 'method-choice'
    timeline.addCard('auth-choice')
  }

  // Re-open the auth-choice card and return to the method-choice sub-phase after a Google
  // attempt ends without an account (popup blocked / cancelled / error / account_exists).
  // CGUA acceptance is kept (cguAccepted stays true) so the visitor doesn't have to
  // re-consent to retry. An optional `errorMessage` is forwarded to the card, which renders
  // it in place of the normal "already have an account" line (see AuthChoiceCard).
  function backToAuthChoice(errorMessage?: string): void {
    authMethod.value = null
    signupSubPhase.value = 'method-choice'
    timeline.addCard('auth-choice', errorMessage ? { errorMessage } : {})
  }

  // OAuth success mirrors the email/long-form terminal: the backend already created AND
  // signed in the account, and the popup's success message carries the server's redirectUrl,
  // which becomes the success screen's primary destination (same as the email path) — the
  // visitor sees the success screen and its CTA lands where a classic signup would.
  // skipTracking: the OmniAuth callback already emitted `signup` +
  // `finished-signup-step-registration` server-side (carrying funnel_variant) for the Google
  // flow, so re-emitting client-side would double-fire — the email path keeps its client emit.
  async function onOAuthSuccess(redirectUrl: string): Promise<void> {
    accountRedirectUrl.value = redirectUrl
    await finishSignup({ skipTracking: true })
  }

  async function chooseGoogle(): Promise<void> {
    if (submitInFlight.value) return
    submitInFlight.value = true
    authMethod.value = 'google'
    timeline.removeFirstOfKind('auth-choice')
    stepStartIdx['signup-method'] = timeline.messages.value.length
    // Captured so every non-success outcome below can remove this bubble before re-showing
    // the choice card — otherwise it dangles above the card the visitor is asked to retry.
    const googleBubbleId = timeline.addUser('Continuer avec Google', {
      step: 'signup-method',
      icon: GOOGLE_ICON,
    })
    signupSubPhase.value = 'oauth-google'
    // Captured before the popup round-trip: a back-navigation meanwhile bumps the generation
    // and already re-showed the right card, so a late non-success outcome must add nothing.
    const gen = timeline.currentGen()

    // Open the popup SYNCHRONOUSLY — before any await — or the browser's popup blocker
    // rejects it: a window.open issued after an await is no longer tied to the user gesture.
    const popup = window.open('', SIGNUP_OAUTH_WINDOW_NAME, oauthPopupFeatures())
    if (!popup) {
      timeline.removeById(googleBubbleId)
      timeline.addBot(GOOGLE_POPUP_BLOCKED)
      backToAuthChoice()
      submitInFlight.value = false
      return
    }

    // FE sends chatbot vocab; the OAuth URL, however, carries the BACKEND universe (the
    // callback reads omniauth.params['universe'] raw) plus the oauth_chatbot=1 routing flag.
    const oauthUrl = `/auth/google_oauth2?universe=${encodeURIComponent(translateUniverse(universe.value))}&oauth_chatbot=1`

    // Prime the server-side OAuth state, then navigate the popup and await its result.
    // Navigation is a POST form (submitOAuthForm), not a GET — OmniAuth's request phase
    // is POST-only, so a bare `popup.location.href = oauthUrl` 404s.
    const result = await runGoogleOAuthPopup(popup, {
      prime: () =>
        api.primeOAuth({
          universe: universe.value,
          role: role.value,
          care_type: typeGarde.value,
          address: addressGeo.value,
          terms_of_use: cguAccepted.value,
          funnel_variant: getFunnelVariant(),
        }),
      navigate: () => submitOAuthForm(oauthUrl, readCsrfMetaToken()),
      expectedOrigin: window.location.origin,
    })

    if (result.status === 'success') {
      // The backend created and signed in the account whatever happened to the chat since:
      // always hand off, even after a rewind.
      await onOAuthSuccess(result.redirectUrl)
    } else if (timeline.isAborted(gen)) {
      return
    } else if (result.status === 'account_exists') {
      // Re-show the auth-choice card (NOT the email form): the visitor may want to retry
      // with a different Google account (the Google button stays available) or log in — the
      // "compte existe déjà" error replaces the card's normal login line (AuthChoiceCard).
      timeline.removeById(googleBubbleId)
      backToAuthChoice(EMAIL_ALREADY_EXISTS)
    } else {
      // 'error' surfaces an in-chat failure bubble; a plain 'cancelled' (visitor closed the
      // popup) stays quiet. Both remove the dangling Google bubble first, then route back to
      // the auth-choice card for a retry.
      timeline.removeById(googleBubbleId)
      if (result.status === 'error') timeline.addBot(SIGNUP_FAILED)
      backToAuthChoice()
    }
    submitInFlight.value = false
  }

  async function chooseEmail(): Promise<void> {
    authMethod.value = 'email'
    timeline.removeFirstOfKind('auth-choice')
    stepStartIdx['signup-method'] = timeline.messages.value.length
    timeline.addUser('✉️ Continuer avec un email', { step: 'signup-method' })
    signupSubPhase.value = 'email-form'
    const gen = timeline.currentGen()
    await timeline.chainMessages([EMAIL_FORM_INTRO], { gen })
    if (timeline.isAborted(gen)) return
    timeline.addCard('email-form')
  }

  async function submitEmail(credentials: { email: string; password: string }): Promise<void> {
    if (submitInFlight.value) return
    submitInFlight.value = true
    emailError.value = null
    email.value = credentials.email
    password.value = credentials.password

    // Duplicate-account guard: inline error + login link, keep the form open.
    const check = await api.checkEmailExists(credentials.email)
    if (check.ok && check.data?.exists) {
      emailError.value = EMAIL_ALREADY_EXISTS
      submitInFlight.value = false
      return
    }

    // Create the account (moved here from the removed opt-in step). On failure, show an
    // error bubble and keep the form for a retry (the form's button is disabled while
    // submitInFlight). FE sends chatbot vocab; the backend translates + builds the STI role.
    const result = await api.createAccount({
      universe: universe.value,
      role: role.value,
      email: email.value,
      password: password.value,
      terms_of_use: cguAccepted.value,
      ...geoToRegistrationPayload(addressGeo.value),
    })
    if (!result.ok) {
      // Surface the backend's specific reason inline on the form (e.g. "n'est pas un e-mail
      // valide") instead of a generic bubble, so the visitor knows what to fix. result.error
      // always carries a message (the API envelope falls back to a generic one on a bare
      // network failure). No login link is shown for it — the "already have an account" case is
      // caught earlier by checkEmailExists, so a createAccount failure is never a duplicate.
      emailError.value = result.error ?? SIGNUP_FAILED
      submitInFlight.value = false
      return
    }

    // Captured for the success screen's primary CTA — the backend's post-signup
    // redirect is the same landing a classic web signup reaches.
    accountRedirectUrl.value = result.data?.redirectUrl ?? null

    timeline.addUser(`✉️ ${credentials.email}`)
    timeline.removeFirstOfKind('email-form')
    await timeline.chainMessages([EMAIL_ACCOUNT_CREATED])
    await sleep(400)
    await finishSignup()
    submitInFlight.value = false
  }

  function alreadyHaveAccount(): void {
    if (submitInFlight.value) return
    void tracking.trackStep('finished-signup-step-exit-already-have-account', snapshot())
    if (typeof window !== 'undefined') window.location.href = '/login'
  }

  return {
    acceptCgua,
    chooseGoogle,
    chooseEmail,
    submitEmail,
    alreadyHaveAccount,
  }
}
